import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { FilterBar, FilterSearch } from "@/components/cinch/FilterBar";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fmtDateTime, fmtDisplay } from "@/lib/date";
import { useUsers, type UserRow } from "@/lib/rbac";

import { UserDrawer } from "../components/UserDrawer";

type SortKey = "name" | "email" | "status" | "last_login" | "created";
interface SortState {
  key: SortKey;
  dir: "asc" | "desc";
}

/** Date comparator that pins nulls to the bottom in either direction. */
function compareDates(a: string | null, b: string | null, dir: "asc" | "desc"): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const diff = new Date(a).getTime() - new Date(b).getTime();
  return dir === "asc" ? diff : -diff;
}

export function UsersPage() {
  const users = useUsers();
  const navigate = useNavigate();
  const search = useSearch({ from: "/system/users" });
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortState | null>({ key: "name", dir: "asc" });

  function toggleSort(key: SortKey) {
    setSort((cur) => {
      if (cur?.key !== key) return { key, dir: "asc" };
      if (cur.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  }
  const sortDir = (key: SortKey) => (sort?.key === key ? sort.dir : null);

  const rows = useMemo(() => {
    let out = users.data ?? [];
    const needle = q.trim().toLowerCase();
    if (needle) {
      out = out.filter(
        (u) =>
          u.name.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle),
      );
    }
    if (!sort) return out;
    const copy = [...out];
    copy.sort((a, b) => {
      const flip = sort.dir === "asc" ? 1 : -1;
      switch (sort.key) {
        case "name":
          return flip * (a.name || a.email).localeCompare(b.name || b.email);
        case "email":
          return flip * a.email.localeCompare(b.email);
        case "status":
          return flip * (Number(b.is_active) - Number(a.is_active));
        case "last_login":
          return compareDates(a.last_login, b.last_login, sort.dir);
        case "created":
          return compareDates(a.created_at, b.created_at, sort.dir);
      }
    });
    return copy;
  }, [users.data, q, sort]);

  const select = (u: UserRow) =>
    navigate({ to: "/system/users", search: { u: u.id } });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Users</h1>
        <p className="text-sm text-muted-foreground">
          Everyone who has signed in to cinch_ops. Click a row to manage roles,
          status, and permissions.
        </p>
      </div>

      <FilterBar>
        <FilterSearch placeholder="Search name or email…" value={q} onChange={setQ} />
      </FilterBar>

      {users.isLoading && <p className="text-sm text-muted-foreground">Loading users…</p>}
      {users.error && (
        <p className="text-sm text-destructive">{String(users.error)}</p>
      )}

      {users.data && (
        <Card>
          <CardContent className="p-0">
            <Table containerClassName="max-h-[calc(100vh-16rem)]">
              <TableHeader>
                <TableRow>
                  <TableHead sortable sortDirection={sortDir("name")} onSort={() => toggleSort("name")}>
                    Name
                  </TableHead>
                  <TableHead sortable sortDirection={sortDir("email")} onSort={() => toggleSort("email")}>
                    Email
                  </TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead sortable sortDirection={sortDir("status")} onSort={() => toggleSort("status")}>
                    Status
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortDir("last_login")}
                    onSort={() => toggleSort("last_login")}
                  >
                    Last login
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortDir("created")}
                    onSort={() => toggleSort("created")}
                  >
                    Created
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody zebra>
                {rows.length === 0 && <TableEmpty colSpan={6}>No users match.</TableEmpty>}
                {rows.map((u) => (
                  <TableRow
                    key={u.id}
                    clickable
                    selected={u.id === search.u}
                    onClick={() => select(u)}
                  >
                    <TableCell className="font-medium">{u.name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {u.roles.length === 0 && (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                        {u.roles.map((r) => (
                          <Badge key={r.id} variant="neutral">
                            {r.name}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge value={u.is_active ? "active" : "inactive"} />
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">
                      {fmtDateTime(u.last_login)}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">
                      {fmtDisplay(new Date(u.created_at))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <UserDrawer />
    </div>
  );
}
